import MentalSpinnerView from 'components/foundation/progress/MentalSpinnerView';
import MentalText from 'components/foundation/typography/MentalText';
import React, { useState } from 'react';
import grid from 'styles/grid';
import pallette from 'styles/pallette';

// Types for button roles and visual modes
const visualModes = ['enabled', 'pressed', 'disabled'];
type VisualMode = typeof visualModes[number];

const buttonRoles = [
  'primary',
  'secondary',
  'tertiary',
  'pill',
  'pill-square',
  'pill-wide',
  'pill-block',
  'link',
];
type ButtonRole = typeof buttonRoles[number];

type Props = {
  title?: string,
  titleLeft?: string,
  titleRight?: string,
  dark?: boolean,
  role: ButtonRole,
  style?: React.CSSProperties,
  containerStyle?: React.CSSProperties,
  isChosen?: boolean,
  iconAfterTitle?: (color: string) => React.ReactNode,
  onPress?: () => void,
  href?: string,
  disabled?: boolean,
  isTitleTextBold?: boolean,
  isTitleAbsolute?: boolean,
  loading?: boolean,
  fullWidth?: number | null,
  containerClassName?: string,
  titleFontSize?: string,
  buttonType? : 'button' | 'submit',
};

const MentalButton = ({
  onPress,
  href,
  role,
  style,
  containerStyle,
  disabled,
  title,
  titleLeft,
  titleRight,
  iconAfterTitle,
  dark,
  isTitleTextBold,
  isTitleAbsolute,
  isChosen,
  loading,
  fullWidth,
  containerClassName,
  titleFontSize,
  buttonType = 'button',
}: Props) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false); // Add this line

  const visualMode: VisualMode = (() => {
    if (disabled) return 'disabled';
    if (isPressed) return 'pressed';
    return 'enabled';
  })();

  const isSecondaryButton = role === 'secondary';
  const isPillButton = role === 'pill' || role === 'pill-square' || role === 'pill-wide' || role === 'pill-block';

  const getMentalTextCharacteristics = () => {
    // Adjusted for web components
    switch (role) {
      case 'primary':
        return { component: MentalText.Subhead2, isBold: true };
      case 'secondary':
        return { component: MentalText.Subhead2, isBold: false };
      case 'pill':
      case 'pill-square':
      case 'pill-wide':
      case 'pill-block':
        return { component: MentalText.ParagraphLarge, isBold: false };
      default:
        return { component: MentalText.Subhead2, isBold: false };
    }
  };

  const getTextColor = () => {
    const isDisabled = visualMode === 'disabled';

    if (isPillButton) {
      if (isDisabled || isChosen) return pallette.tertiary.w1000;
      return dark ? pallette.neutral.w300 : pallette.tertiary.w1000;
    }

    switch (role) {
      case 'primary':
        return pallette.tertiary.w1000;
      case 'secondary':
        return isDisabled ? pallette.neutral.w500 : pallette.primary.w200;
      case 'tertiary':
        return isDisabled ? pallette.neutral.w300 : pallette.neutral.w100;
      case 'link':
        return pallette.accent.link;
      default:
        return pallette.tertiary.w1000;
    }
  };

  const getBackgroundStyle = () => {
    // Background styles for web
    const backgroundColor = () => {
      switch (role) {
        case 'primary':
          if (visualMode === 'pressed') return pallette.primary.w400;
          if (visualMode === 'disabled') return pallette.neutral.w700;
          return pallette.primary.w500;
        case 'secondary':
          if (visualMode === 'pressed') return pallette.tertiary.w1000;
          if (visualMode === 'disabled') return pallette.neutral.w800;
          return pallette.tertiary.w800;
        case 'tertiary':
          if (visualMode === 'pressed') return pallette.tertiary.w600;
          if (visualMode === 'disabled') return pallette.neutral.w700;
          return pallette.tertiary.w800;
        case 'pill':
        case 'pill-square':
        case 'pill-wide':
        case 'pill-block':
          if (visualMode === 'disabled') return pallette.neutral.w700;
          if (visualMode === 'pressed') return dark ? pallette.neutral.w800 : pallette.primary.w300;
          if (isChosen) return pallette.primary.w400;
          return dark ? pallette.neutral.w900 : pallette.primary.w100;
        case 'link':
          return 'transparent';
        default:
          return '';
      }
    };

    const borderRadius = !title ? grid.Small : grid.XXSmall;

    return {
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius,
      backgroundColor: role === 'tertiary' && isHovered && visualMode === 'enabled'
        ? pallette.tertiary.w600
        : backgroundColor(),
      marginRight: 'auto',
      marginLeft: 'auto',
      paddingRight: grid.Large,
      paddingLeft: grid.Large,
      paddingTop: grid.Custom.d12,
      paddingBottom: grid.Custom.d12,
      opacity: visualMode === 'disabled' ? 0.7 : 1,
      borderColor: visualMode === 'disabled' ? pallette.neutral.w700 : pallette.neutral.w300,
      borderWidth: isSecondaryButton ? '1px' : undefined,
      borderStyle: isSecondaryButton ? 'solid' : undefined,
      width: fullWidth && fullWidth > 0 ? '100%' : undefined,
    };
  };

  const { component: TextComponent, isBold } = getMentalTextCharacteristics();

  const renderInnerContents = () => {
    if (loading) {
      return (
        <div className="flex justify-center">
          <MentalSpinnerView dimension={grid.Medium} mode="dark" />
        </div>
      );
    }

    const isTextBold = isBold || isTitleTextBold;
    const titleStyle = {
      ...(isTitleAbsolute ? { position: 'absolute' as 'absolute' } : {}),
      textAlign: 'center' as 'center',
      fontSize: titleFontSize,
    };

    if (titleLeft && titleRight) {
      return (
        <>
          <TextComponent bold={isTextBold} color={getTextColor()} style={titleStyle}>{titleLeft}</TextComponent>
          <TextComponent bold={isTextBold} color={getTextColor()} style={titleStyle}>{titleRight}</TextComponent>
        </>
      );
    }
    if (title) {
      if (iconAfterTitle) {
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          >
            <TextComponent bold={isTextBold} color={getTextColor()} style={titleStyle}>{title}</TextComponent>
            {iconAfterTitle(getTextColor())}
          </div>
        );
      }
      return <TextComponent bold={isTextBold} color={getTextColor()} style={titleStyle}>{title}</TextComponent>;
    }
    return null;
  };

  const touchesDisabled = disabled || loading;

  const width = (() => {
    if (fullWidth === null) return undefined;
    if (fullWidth) return `min(${fullWidth}px, calc(100vw - 32px))`;
    return style?.width ?? grid.XXXXLarge;
  })();

  return (
    <div
      className={containerClassName}
      style={{
        alignItems: 'center',
        width,
        ...containerStyle,
      }}
    >
      {
        href ? (
          <a
            href={href}
            // disabled={touchesDisabled}
            onMouseDown={() => !touchesDisabled && setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onClick={() => {
              if (touchesDisabled) return;
              if (!onPress) return;
              onPress();
            }}
            style={{
              ...getBackgroundStyle(),
              ...style,
              display: 'block',
            }}
          >
            {renderInnerContents()}
          </a>
        ) : (
          <button
            type={buttonType}
            disabled={touchesDisabled}
            onMouseDown={() => !touchesDisabled && setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
              if (touchesDisabled) return;
              if (!onPress) return;
              onPress();
            }}
            style={{ ...getBackgroundStyle(), ...style }}
          >
            {renderInnerContents()}
          </button>
        )
      }
    </div>
  );
};

export default MentalButton;
